import React from 'react';
import getProp from 'lodash/get';
import setProp from 'lodash/set';
import reduce from 'lodash/reduce';
import { FILTER_TYPE } from '../../constants/data';

/**
  Convert a JS object into a list by using the values
  @function mapToList
  @param {Object} map Object to convert
  @returns {List}
*/
export const mapToList = map =>
  reduce(map, (list, item) => list.concat(item), []);

/**
  Convert a list into a JS object
  @function listToMap
  @param {List} list List to convert into a map
  @param {String} propName Property to use as key
  @returns {Object}
*/
export const listToMap = (list, propName = 'id') => {
  return list.reduce(
    (map, item) => ({
      ...map,
      [item[propName].toLowerCase()]: item
    }),
    {}
  );
};

/**
  Populate a property by obtaining the value from a map
  @function populateFromMap
  @param {List} list List of objects to populate
  @param {String} propertyName Property to populate
  @param {Object} mapById Map of objects to use to populate
  @param {String} idPropName Property name to match as the key
  @returns {List}
*/
export const populateFromMap = (
  list,
  propertyName,
  mapById,
  idPropName = 'id'
) => {
  return list.map(item => {
    let value = getProp(item, propertyName);
    let propValue =
      value && value[idPropName] && value[idPropName].toLowerCase();
    if (propValue && propValue in mapById) {
      setProp(item, propertyName, mapById[propValue]);
    }
    return item;
  });
};

/**
  Access data according to the column info
  @function accessColumnData
  @param {Object} item
  @param {Object} column
  @returns {String}
*/
export const accessColumnData = (item, column) =>
  (typeof column.accessor === 'function'
    ? column.accessor(item)
    : getProp(item, column.accessor)) || '';

/**
  Determine if an item passes the filter
  @function filterByColumn
  @param {Object} item
  @param {Object} column
  @param {Object} filter
  @returns {Boolean}
*/
const filterByColumn = (item, column, filter) => {
  let d = column.searchable
    ? accessColumnData(item, column)
      .toLowerCase()
      .includes(filter.value.toLowerCase())
    : false;
  return d;
}

/**
  Determine if an item passes the filter using all columns
  @function filterByColumn
  @param {Object} item
  @param {List} columns
  @param {Object} filter
  @returns {Boolean}
*/
const filterAllColumns = (item, columns, filter) =>
  columns.reduce(
    (result, column) => result || filterByColumn(item, column, filter),
    false
  );

/**
  Determine if an item passes the filter using just the filter
  @function filterByFilter
  @param {Object} item
  @param {Object} filter
  @returns {Boolean}
*/
const filterByFilter = (item, filter) => {
  return accessColumnData(item, filter)
    .toLowerCase()
    .includes(filter.value.toLowerCase());
};

/**
  Get a list of items that pass the filter
  @function filter
  @param {List} list Objects to filter
  @param {List} filters
  @param {Object} columnsToFilter
  @returns {Boolean}
*/
export const filter = (list, filters, columnsToFilter) => {
  return list.filter(item =>
    filters.reduce(
      (result, filter) =>
        result &&
        (filter.type === FILTER_TYPE.SEARCH
          ? filterAllColumns(item, columnsToFilter, filter)
          : filterByFilter(item, filter)),
      true
    )
  );
};

/**
  Create component according to the column info
  @function getColumnData
  @param {Object} item
  @param {Object} column
  @returns {Component}
*/
export const getColumnData = (data, column, props) => {
  let value = '';
  if (typeof column.accessor === 'function') {
    value = column.accessor(data);
  } else {
    value = getProp(data, column.accessor);
  }
  if (column.Cell) {
    value = <column.Cell value={value} {...props} />;
  }
  return value;
};


/**
  Extract and parse query params for tags/expor
  @function getUrlByQueryParms
  @param {Object} queryParams
  @returns {String}
*/
export const getUrlByQueryParms = (queryParams) => {
  let urlValue = '';
  if(queryParams.searchText){
    urlValue=`${urlValue}&search=${encodeURIComponent(queryParams.searchText)}`;
  }
  if (queryParams.page && queryParams.size) {
    urlValue = `${urlValue}&page=${queryParams.page}&size=${queryParams.size}`;
  }
  if(queryParams.sorting) {
    const { column: { sortField }, descendingOrder } = queryParams.sorting;
    urlValue = `${urlValue}&sortBy=${sortField}&sort=${descendingOrder?'dsc':'asc'}`
  }
  if (queryParams.areaId) {
    urlValue = `${urlValue}&areaId=eq:${queryParams.areaId}`
  }
  if (queryParams.groupBy) {
    urlValue = `${urlValue}&groupBy=${queryParams.groupBy}`
  }
  if (queryParams.filters && queryParams.filters.length) {
    queryParams.filters.forEach(filter => {
      if(filter.key === 'batteryStatus'){
        urlValue = `${urlValue}&${filter.key}=eq:${filter.value === 'Good' ? 1 : 0}`;
        return;
      }
      if(filter.key === 'locationName'){
        urlValue = `${urlValue}&${filter.key}=cs:${filter.value}`;
        return;
      }
      urlValue = `${urlValue}&${filter.key}=eq:${filter.value}`;
    });
  }
  if(queryParams.format){
    urlValue = `${urlValue}&format=${queryParams.format}`;
  }
  return urlValue;
};

/**
 * Map Tag Location Names to exclude the '@' symbol and anything after it
 * @function mapTagsLocationName
 * @param {List} tags
 * @returns {List}
 */
export const mapTagsLocationName = (tags) =>
  tags.map((tag) =>
    ({
      ...tag,
      locationName: (tag.locationName && tag.locationName.split('@')[0]) || tag.locationName,
      location: tag.location && {
        ...tag.location,
        name: (typeof tag.location.name === 'string' && tag.location.name.split('@')[0]) || tag.location.name
      }
    })
  );


// WEBPACK FOOTER //
// ./src/utils/transform/index.js